import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  BlogArticle,
  BlogArticlesResponse,
  BlogStatsResponse,
  CreateBlogArticleDto,
  UpdateBlogArticleDto,
  GetBlogArticlesParams,
  UploadImageResponse,
  SlugCheckResponse
} from 'types/interfaces/blog';

export const blogApi = createApi({
  reducerPath: 'blogApi',
  baseQuery: baseQuery,
  tagTypes: ['BlogArticles', 'BlogArticle', 'BlogStats'],
  endpoints: (builder) => ({
    // ===== PUBLIC ENDPOINTS =====

    // Get published articles (public)
    getPublishedArticles: builder.query<BlogArticlesResponse, GetBlogArticlesParams>({
      query: (params = {}) => {
        const { page = 1, limit = 10, search = '', sortBy = 'publishedAt', sortOrder = 'desc' } = params;
        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy,
          sortOrder,
        });
        if (search) queryParams.append('search', search);

        return {
          url: `/blog/articles?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: any) => ({
        data: response.data,
        pagination: response.pagination,
      }),
      providesTags: (result) =>
        result && result.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'BlogArticles' as const, id })),
              { type: 'BlogArticles', id: 'LIST' },
            ]
          : [{ type: 'BlogArticles', id: 'LIST' }],
    }),

    // Get single published article by slug (public)
    getArticleBySlug: builder.query<BlogArticle, string>({
      query: (slug) => ({
        url: `/blog/articles/${slug}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response.data,
      providesTags: (result, error, slug) => [{ type: 'BlogArticle', id: slug }],
    }),

    // Get recent articles (public)
    getRecentArticles: builder.query<BlogArticle[], number | void>({
      query: (limit = 5) => ({
        url: `/blog/recent?limit=${limit}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response.data,
      providesTags: ['BlogArticles'],
    }),

    // ===== ADMIN ENDPOINTS =====

    // Get all articles (admin - includes drafts)
    getAllArticles: builder.query<BlogArticlesResponse, GetBlogArticlesParams>({
      query: (params = {}) => {
        const { page = 1, limit = 10, status, search = '', sortBy = 'createdAt', sortOrder = 'desc' } = params;
        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy,
          sortOrder,
        });
        if (status) queryParams.append('status', status);
        if (search) queryParams.append('search', search);

        return {
          url: `/admin/blog/articles?${queryParams.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      transformResponse: (response: any) => ({
        data: response.data,
        pagination: response.pagination,
      }),
      providesTags: (result) =>
        result && result.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'BlogArticles' as const, id })),
              { type: 'BlogArticles', id: 'ADMIN_LIST' },
            ]
          : [{ type: 'BlogArticles', id: 'ADMIN_LIST' }],
    }),

    // Get single article by ID (admin)
    getArticleById: builder.query<BlogArticle, string>({
      query: (id) => ({
        url: `/admin/blog/articles/${id}`,
        method: 'GET',
        requiresAuth: true,
      }),
      transformResponse: (response: any) => response.data,
      providesTags: (result, error, id) => [{ type: 'BlogArticle', id }],
    }),

    // Create article (admin)
    createArticle: builder.mutation<BlogArticle, CreateBlogArticleDto>({
      query: (body) => ({
        url: '/admin/blog/articles',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      transformResponse: (response: any) => response.data,
      invalidatesTags: [{ type: 'BlogArticles', id: 'ADMIN_LIST' }, 'BlogStats'],
    }),

    // Update article (admin)
    updateArticle: builder.mutation<BlogArticle, { id: string; data: UpdateBlogArticleDto }>({
      query: ({ id, data }) => ({
        url: `/admin/blog/articles/${id}`,
        method: 'PUT',
        body: data,
        requiresAuth: true,
      }),
      transformResponse: (response: any) => response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'BlogArticle', id },
        { type: 'BlogArticles', id: 'ADMIN_LIST' },
        { type: 'BlogArticles', id: 'LIST' },
        'BlogStats',
      ],
    }),

    // Delete article (admin)
    deleteArticle: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/admin/blog/articles/${id}`,
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: [
        { type: 'BlogArticles', id: 'ADMIN_LIST' },
        { type: 'BlogArticles', id: 'LIST' },
        'BlogStats',
      ],
    }),

    // Publish article (admin)
    publishArticle: builder.mutation<BlogArticle, string>({
      query: (id) => ({
        url: `/admin/blog/articles/${id}/publish`,
        method: 'POST',
        requiresAuth: true,
      }),
      transformResponse: (response: any) => response.data,
      invalidatesTags: (result, error, id) => [
        { type: 'BlogArticle', id },
        { type: 'BlogArticles', id: 'ADMIN_LIST' },
        { type: 'BlogArticles', id: 'LIST' },
        'BlogStats',
      ],
    }),

    // Unpublish article (admin)
    unpublishArticle: builder.mutation<BlogArticle, string>({
      query: (id) => ({
        url: `/admin/blog/articles/${id}/unpublish`,
        method: 'POST',
        requiresAuth: true,
      }),
      transformResponse: (response: any) => response.data,
      invalidatesTags: (result, error, id) => [
        { type: 'BlogArticle', id },
        { type: 'BlogArticles', id: 'ADMIN_LIST' },
        { type: 'BlogArticles', id: 'LIST' },
        'BlogStats',
      ],
    }),

    // Upload image (admin)
    uploadBlogImage: builder.mutation<UploadImageResponse, FormData>({
      query: (formData) => ({
        url: '/admin/blog/upload-image',
        method: 'POST',
        body: formData,
        requiresAuth: true,
        // Don't set Content-Type header - let browser set it with boundary
      }),
      transformResponse: (response: any) => ({ data: response.data }),
    }),

    // Get blog statistics (admin)
    getBlogStats: builder.query<BlogStatsResponse, void>({
      query: () => ({
        url: '/admin/blog/stats',
        method: 'GET',
        requiresAuth: true,
      }),
      transformResponse: (response: any) => ({ data: response.data }),
      providesTags: ['BlogStats'],
    }),

    // Check slug availability (admin)
    checkSlugAvailability: builder.query<SlugCheckResponse, string>({
      query: (slug) => ({
        url: `/admin/blog/check-slug/${slug}`,
        method: 'GET',
        requiresAuth: true,
      }),
      transformResponse: (response: any) => ({ data: response.data }),
    }),
  }),
});

export const {
  // Public hooks
  useGetPublishedArticlesQuery,
  useGetArticleBySlugQuery,
  useGetRecentArticlesQuery,

  // Admin hooks
  useGetAllArticlesQuery,
  useGetArticleByIdQuery,
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useDeleteArticleMutation,
  usePublishArticleMutation,
  useUnpublishArticleMutation,
  useUploadBlogImageMutation,
  useGetBlogStatsQuery,
  useLazyCheckSlugAvailabilityQuery,
  useLazyGetArticleByIdQuery,
} = blogApi;
